import React, { useState, useEffect, useRef } from 'react';
import {
  LuTrash2,
  LuCheck,
  LuAlertCircle,
  LuSettings,
  LuEye,
  LuEyeOff,
  LuLoader2
} from 'react-icons/lu';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../../firebaseConfig'; // Adjust path as needed
import FieldMappingInterface from '../../../../components/FieldMappingInterface';
import { getAvailableModuleTypes, getModuleSchema, validateFieldMapping } from '../../../../config/moduleSchemas';

const Settings = ({ node, moduleName, institution }) => {
  const [showSecrets, setShowSecrets] = useState(false);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [fieldMappingValidation, setFieldMappingValidation] = useState({ isValid: false });

  // Use refs to track auto-save
  const saveTimeoutRef = useRef(null);
  const configRef = useRef({});
  const isInitialLoadRef = useRef(true);

  // Check if all required fields are filled based on node type
  const checkConfigurationComplete = (configData, nodeType) => {
    switch (nodeType) {
      case 'database':
        // Database nodes are always considered configured (no editable fields)
        return true;

      case 'agent':
        return !!(
          configData.model &&
          configData.apiKey &&
          configData.systemPrompt &&
          configData.temperature !== undefined &&
          configData.maxTokens
        );

      case 'worker':
        if (!configData.scrapeURL) return false;
        
        // Get module type from the module name (e.g., "events" -> events module)
        const moduleType = moduleName; // moduleName is passed as prop and indicates the module type
        
        // Validate field mappings if they exist
        if (configData.fieldMappings && moduleType) {
          const validation = validateFieldMapping(moduleType, configData.fieldMappings);
          return validation.isValid;
        }
        
        return false;

      case 'server':
        // Server nodes are always considered configured (no editable fields)
        return true;

      default:
        return false;
    }
  };

  // Load configuration from Firestore
  useEffect(() => {
    const loadConfig = async () => {
      if (!institution?.id || !moduleName || !node?.id) return;

      try {
        setLoading(true);
        setError(null);

        const docRef = doc(db, `colleges/${institution.id}/configs/${moduleName}/nodes/${node.id}`);
        const configSnap = await getDoc(docRef);

        if (configSnap.exists()) {
          const configData = configSnap.data();
          // Remove status from config data for form display
          const { status, ...configOnly } = configData;
          setConfig(configOnly || {});
          configRef.current = configOnly || {};
        } else {
          // Initialize with empty config and set initial status
          setConfig({});
          configRef.current = {};

          // Set initial status to not_configured
          const initialData = { status: 'not_configured' };
          await setDoc(docRef, initialData, { merge: true });
        }
      } catch (err) {
        console.error('Error loading config:', err);
        setError('Failed to load configuration');
      } finally {
        setLoading(false);
        setInitialLoad(false);
        isInitialLoadRef.current = false;
      }
    };

    loadConfig();
  }, [institution?.id, moduleName, node?.id]);

  // Auto-save function
  const autoSave = async (newConfig) => {
    if (!institution?.id || !moduleName || !node?.id || isInitialLoadRef.current) return;

    try {
      setSaving(true);
      setError(null);

      // Check if configuration is complete
      const isComplete = checkConfigurationComplete(newConfig, node?.data?.type);
      const status = isComplete ? 'configured' : 'not_configured';

      // Save both config and status
      const dataToSave = {
        ...newConfig,
        status: status
      };

      const docRef = doc(db, `colleges/${institution.id}/configs/${moduleName}/nodes/${node.id}`);
      await setDoc(docRef, dataToSave, { merge: true });

      configRef.current = newConfig;
      setSaved(true);

      // Hide the "saved" indicator after 2 seconds
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error auto-saving config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Debounced auto-save effect
  useEffect(() => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Only auto-save if config has actually changed and it's not the initial load
    if (!isInitialLoadRef.current && JSON.stringify(config) !== JSON.stringify(configRef.current)) {
      // Set a new timeout for auto-save (500ms delay)
      saveTimeoutRef.current = setTimeout(() => {
        autoSave(config);
      }, 500);
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [config, institution?.id, moduleName, node?.id]);

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const renderConfigFields = () => {
    if (!node) return null;

    const isConfigComplete = checkConfigurationComplete(config, node.data.type);

    return (
      <div className="space-y-6">

        {/* Node Type Specific Fields */}
        {(() => {
          switch (node.data.type) {
            case 'database':
              return (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Hook
                    </label>
                    <input
                      type="text"
                      value={`/${moduleName || 'module'}`}
                      readOnly
                      className="w-full px-3 py-2 bg-[#ffffff05] border border-[#ffffff15] rounded text-white/60 text-sm cursor-not-allowed"
                    />
                    <p className="text-white/40 text-xs mt-1">This field is automatically generated and cannot be edited</p>
                  </div>
                </div>
              );

            case 'agent':
              return (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Model *
                    </label>
                    <select
                      value={config.model || 'gpt-4'}
                      onChange={(e) => handleConfigChange('model', e.target.value)}
                      className="w-full px-3 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm focus:outline-none focus:border-[#ffffff40]"
                    >
                      <option value="">Select a model...</option>
                      <option value="gpt-4">GPT-4</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                      <option value="claude-3">Claude 3</option>
                      <option value="claude-sonnet-4">Claude Sonnet 4</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      API Key *
                    </label>
                    <div className="relative">
                      <input
                        type={showSecrets ? "text" : "password"}
                        value={config.apiKey || ''}
                        onChange={(e) => handleConfigChange('apiKey', e.target.value)}
                        placeholder="Enter API key..."
                        className="w-full px-3 py-2 pr-10 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#ffffff40]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecrets(!showSecrets)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80"
                      >
                        {showSecrets ? <LuEyeOff className="w-4 h-4" /> : <LuEye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      System Prompt *
                    </label>
                    <textarea
                      value={config.systemPrompt || ''}
                      onChange={(e) => handleConfigChange('systemPrompt', e.target.value)}
                      placeholder="Enter system prompt..."
                      rows={4}
                      className="w-full px-3 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#ffffff40] resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Temperature *
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={config.temperature !== undefined ? config.temperature : 0.7}
                      onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-white/60 mt-1">
                      <span>Focused (0)</span>
                      <span className="text-white/80">{config.temperature !== undefined ? config.temperature : 0.7}</span>
                      <span>Creative (1)</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Max Tokens *
                    </label>
                    <input
                      type="number"
                      value={config.maxTokens || ''}
                      onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value) || '')}
                      placeholder="1000"
                      className="w-full px-3 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#ffffff40]"
                    />
                  </div>
                </div>
              );

            case 'worker':
              // Get module type from the module context (e.g., "events", "menus", etc.)
              const moduleType = moduleName; // moduleName prop indicates the module type
              const moduleSchema = moduleType ? getModuleSchema(moduleType) : null;
              
              return (
                <div className="space-y-6">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Role
                    </label>
                    <input
                      type="text"
                      value="Data Scraper"
                      readOnly
                      className="w-full px-3 py-2 bg-[#ffffff05] border border-[#ffffff15] rounded text-white/60 text-sm cursor-not-allowed"
                    />
                    <p className="text-white/40 text-xs mt-1">This field is automatically set and cannot be edited</p>
                  </div>

                  {moduleSchema && (
                    <div className="p-4 bg-[#ffffff05] border border-[#ffffff15] rounded">
                      <h5 className="text-white font-medium mb-2">Module: {moduleSchema.name}</h5>
                      <div className="text-white/80 text-sm mb-2">{moduleSchema.description}</div>
                      <div className="text-white/60 text-xs">
                        Required fields: {moduleSchema.required.map(f => f.label).join(', ')}
                      </div>
                      {moduleSchema.optional.length > 0 && (
                        <div className="text-white/60 text-xs mt-1">
                          Optional fields: {moduleSchema.optional.map(f => f.label).join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Target URL *
                    </label>
                    <input
                      type="url"
                      value={config.scrapeURL || ''}
                      onChange={(e) => handleConfigChange('scrapeURL', e.target.value)}
                      placeholder="https://example.com/data"
                      className="w-full px-3 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#ffffff40]"
                    />
                    <p className="text-white/40 text-xs mt-1">URL of the website to scrape data from</p>
                  </div>

                  {/* Field Mapping Interface */}
                  {moduleType && config.scrapeURL && (
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-3">
                        Field Mapping *
                      </label>
                      <FieldMappingInterface
                        moduleType={moduleType}
                        scrapeURL={config.scrapeURL}
                        fieldMappings={config.fieldMappings || {}}
                        onMappingChange={(mappings) => handleConfigChange('fieldMappings', mappings)}
                        onValidationChange={setFieldMappingValidation}
                      />
                      
                      {!fieldMappingValidation.isValid && fieldMappingValidation.missingFields && (
                        <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded flex items-center gap-2">
                          <LuAlertCircle className="w-4 h-4 text-yellow-400" />
                          <div className="text-yellow-400 text-sm">
                            <div>Missing required field mappings:</div>
                            <div className="text-yellow-300 text-xs mt-1">
                              {fieldMappingValidation.missingFields.join(', ')}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!config.scrapeURL && (
                    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded flex items-center gap-2">
                      <LuAlertCircle className="w-4 h-4 text-blue-400" />
                      <p className="text-blue-400 text-sm">
                        Enter a target URL to configure field mappings for this {moduleSchema?.name || 'module'}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Scraping Frequency
                    </label>
                    <select
                      value={config.frequency || 'manual'}
                      onChange={(e) => handleConfigChange('frequency', e.target.value)}
                      className="w-full px-3 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm focus:outline-none focus:border-[#ffffff40]"
                    >
                      <option value="manual">Manual (Deploy to run once)</option>
                      <option value="hourly">Every Hour</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                    </select>
                    <p className="text-white/40 text-xs mt-1">How often the scraper should run automatically</p>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Output Format
                    </label>
                    <select
                      value={config.outputFormat || 'json'}
                      onChange={(e) => handleConfigChange('outputFormat', e.target.value)}
                      className="w-full px-3 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm focus:outline-none focus:border-[#ffffff40]"
                    >
                      <option value="json">JSON</option>
                      <option value="csv">CSV</option>
                      <option value="text">Plain Text</option>
                    </select>
                    <p className="text-white/40 text-xs mt-1">Format for the scraped data output</p>
                  </div>
                </div>
              );

            case 'server':
              return (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Audience
                    </label>
                    <input
                      type="text"
                      value="Students"
                      readOnly
                      className="w-full px-3 py-2 bg-[#ffffff05] border border-[#ffffff15] rounded text-white/60 text-sm cursor-not-allowed"
                    />
                    <p className="text-white/40 text-xs mt-1">This field is automatically set and cannot be edited</p>
                  </div>
                </div>
              );

            default:
              return (
                <div className="p-4 bg-[#ffffff05] rounded-lg border border-[#ffffff10] text-center">
                  <LuSettings className="w-8 h-8 text-white/40 mx-auto mb-2" />
                  <p className="text-white/60 text-sm">
                    No configuration options available for this node type.
                  </p>
                </div>
              );
          }
        })()}
      </div>
    );
  };

  if (initialLoad && loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/60">
          <LuLoader2 className="w-5 h-5 animate-spin" />
          <span>Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 relative">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-white font-medium text-lg flex items-center gap-2">
          <LuSettings className="w-5 h-5" />
          Settings
        </h3>
        <p className="text-white/60 text-sm mt-1">
          Configure the behavior and parameters for this {node?.data?.type} node
        </p>
      </div>

      {/* Configuration Form */}
      <div className="space-y-6">
        {renderConfigFields()}

        {/* Required Fields Note */}
        <div className="text-white/40 text-xs">
          * Required fields
        </div>
      </div>

      {saving && (
        <div className="absolute right-6 top-6 flex items-center gap-2 text-blue-400 text-sm">
          <LuLoader2 className="w-4 h-4 animate-spin" />
        </div>
      )}

      {saved && (
        <div className="absolute right-6 top-6 flex items-center gap-2 text-green-400 text-sm">
          <LuCheck className="w-4 h-4" />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
          <LuAlertCircle className="w-4 h-4 text-red-400" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default Settings;